import { ReactNode, InputHTMLAttributes, ButtonHTMLAttributes, forwardRef } from 'react';
import { Link } from 'react-router-dom';

/**
 * `Reveal` and `CountUp` were here until the UI audit of 2026-09-21 and are now
 * in `features/landing/motion.tsx`. They are marketing motion - a 700ms scroll
 * entrance and a counting number - and living in `shared/ui` put them one
 * import away from screens a student opens ten times a day, where the same
 * animation is a tax rather than a welcome.
 */

export const TextField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & {
    icon?: string;
    trailing?: ReactNode;
  }
>(function TextField({ icon, trailing, className = '', ...props }, ref) {
  return (
    <div className="relative group">
      {icon && (
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <span className="material-symbols-outlined text-on-surface-variant group-focus-within:text-primary transition-colors text-[22px]">
            {icon}
          </span>
        </div>
      )}
      <input
        ref={ref}
        {...props}
        className={`w-full ${icon ? 'pl-12' : 'pl-4'} ${
          trailing ? 'pr-12' : 'pr-4'
        } py-3.5 bg-surface-container-lowest text-on-surface font-body-md text-body-md rounded-2xl border border-outline-variant focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-[border-color,box-shadow] duration-chip placeholder:text-on-surface-variant ${className}`}
      />
      {trailing && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center">{trailing}</div>
      )}
    </div>
  );
});

export function Button({
  children,
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'outline' | 'ghost';
}) {
  /**
   * DESIGN.md §4 and §7: every button is a pill, "no exception". The primary
   * is the dark one (ink, white label, the tight shadow); the outline is the
   * white "Filled Large"; the ghost is text until hovered. Hover is an ink
   * overlay, never a new colour (§7).
   *
   * No `active:scale`: the base layer of `index.css` gives every button and
   * every `role="button"` the same press feedback, so a per-component one is a
   * second answer to the same question. And the transition names its
   * properties: the blanket form animates ones nobody intended, layout
   * included.
   */
  const base =
    'inline-flex items-center justify-center gap-2 font-body-md text-body-md font-medium rounded-full transition-[box-shadow,background-color,color,opacity] duration-chip ease-out-strong disabled:opacity-50';
  const styles = {
    primary: 'min-h-[48px] px-7 py-3 bg-primary text-on-primary shadow-sm hover:bg-primary/85',
    outline:
      'min-h-[48px] px-7 py-3 bg-surface-container-lowest border border-hairline text-on-surface hover:bg-surface-container-low',
    ghost: 'min-h-[44px] px-4 py-2 text-on-surface hover:bg-primary/[0.06]',
  };
  return (
    <button {...props} className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </button>
  );
}

/**
 * The same four button tokens as `Button`, as navigation. A real `<a>` because
 * a CTA that changes the URL belongs in a link (middle-click, open-in-new-tab,
 * screen-reader link lists) - and a `<button>` that hacks `navigate()` into it
 * is none of those.
 */
export function ButtonLink({
  to,
  variant = 'primary',
  className = '',
  children,
}: {
  to: string;
  variant?: 'primary' | 'outline' | 'ghost';
  className?: string;
  children: ReactNode;
}) {
  const base =
    'inline-flex items-center justify-center gap-2 font-title-md text-title-md rounded-xl transition-[box-shadow,background-color,color] duration-chip ease-out-strong';
  const styles = {
    primary: 'px-6 py-3 bg-primary text-on-primary shadow-sm hover:bg-primary-container',
    outline: 'px-6 py-3 border border-outline-variant text-on-surface hover:bg-surface-container',
    ghost: 'px-4 py-2 text-on-surface-variant hover:bg-surface-container',
  };
  return (
    <Link to={to} className={`${base} ${styles[variant]} ${className}`}>
      {children}
    </Link>
  );
}

export * from './Toast';
export * from './RuleRefusal';
export * from './ModuleSection';
export * from './StarRating';

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  /**
   * Optional on purpose. A subtitle earns its place by saying something the
   * title does not - "Week of 21 September", "3 awaiting your decision". A
   * restatement of the title is filler, and a REQUIRED prop is how filler gets
   * written: every caller had to pass something, so every screen carried a grey
   * sentence saying what its own heading already said.
   */
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="font-headline-xl text-headline-xl text-on-surface">{title}</h1>
        {subtitle && (
          <p className="font-body-md text-body-md text-on-surface-variant mt-1">{subtitle}</p>
        )}
      </div>
      {action && <div className="flex items-center gap-3">{action}</div>}
    </div>
  );
}

/**
 * DESIGN.md §Status. The four status pairs are a SEPARATE language from the six
 * module pastels, "because a colour cannot mean 'this is the Restaurant' and
 * 'this was refused' at once" - and they are tokens, not Tailwind's palette.
 * `bg-amber-100` sitting beside `bg-status-warning` is two systems in one file,
 * which is the loudest sign that nobody owns the colour.
 */
const STATUS_META: Record<string, string> = {
  OPEN: 'bg-status-warning text-on-status-warning',
  RESOLVED: 'bg-status-success text-on-status-success',
  AUTO_RESOLVED: 'bg-status-warning text-on-status-warning',
  CLOSED_WITHOUT_CONFIRMATION: 'bg-status-neutral text-on-status-neutral',
  ARCHIVED: 'bg-status-neutral text-on-status-neutral',
};

/**
 * DESIGN.md §Chips: "Status: pill-shaped, `label` type, one of the four status
 * pairs. **Text only, no icon, no border.**" The icon is what made these read
 * as badges from a template rather than as this system's chips.
 */
export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const classes = STATUS_META[status] ?? 'bg-status-neutral text-on-status-neutral';
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full font-label-sm text-label-sm ${classes}`}
    >
      {label ?? status.replace(/_/g, ' ')}
    </span>
  );
}

/**
 * Six modules ship with empty tables on day one, so this is the first thing
 * most users see in most of the product.
 *
 * It was a dashed grey outline around a circled icon over centred grey text -
 * which is the admin-template look DESIGN.md is defined against, and a dashed
 * border on top of §No Black Border. Depth here comes from a tonal step down
 * (§Elevation), the text is left-aligned like every other block of reading on
 * the screen, and the icon is gone: a decorative medallion says nothing the
 * sentence under it does not.
 *
 * **`icon` is deliberately not a prop any more.** Callers passed one because
 * the signature demanded it, and a prop nobody chooses is a decision nobody
 * made.
 */
export function EmptyState({
  title,
  description,
  action,
  className = '',
}: {
  title: string;
  description?: string;
  /** The next step, when there is one. An empty state that teaches beats one that apologises. */
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex flex-col items-start gap-2 py-10 px-5 bg-surface-container-low rounded-3xl ${className}`}
    >
      <h3 className="font-title-md text-title-md text-on-surface">{title}</h3>
      {description && (
        <p className="font-body-sm text-body-sm text-on-surface-variant max-w-[60ch]">
          {description}
        </p>
      )}
      {action && <div className="mt-3 w-full sm:w-auto">{action}</div>}
    </div>
  );
}
